import {ChangeEvent, HTMLAttributes, useState} from 'react';
import styled from 'styled-components';
import {POLICY_LINKS} from '@/router';
import {Divider, Flexbox, FlexFullGrow} from '@/styled-components';
import {Button} from '../Button';
import {Checkbox, CheckboxItem, CheckboxLabel} from '../Checkbox';
import {Typography, typographyStyle} from '../Typography';
import {InputContainer} from './StyledComponents';

enum CheckType {
  isAgeAgreed = 'isAgeAgreed',
  isServiceAgreed = 'isServiceAgreed',
  isPrivacyAgreed = 'isPrivacyAgreed',
  isMarketingAgreed = 'isMarketingAgreed',
}

interface Values {
  [CheckType.isAgeAgreed]?: boolean;
  [CheckType.isServiceAgreed]?: boolean;
  [CheckType.isPrivacyAgreed]?: boolean;
  [CheckType.isMarketingAgreed]?: boolean;
}

interface IProps extends HTMLAttributes<HTMLDivElement> {
  loading?: boolean;
  onComplete(isMarketingAgreed: boolean): void;
}

export const AgreementStep = ({loading, onComplete, ...props}: IProps) => {
  const [values, setValues] = useState<Values>({});

  const onCheck = (e: ChangeEvent<HTMLInputElement>) => {
    const {checked, name} = e.target;

    if (name === 'allCheck') {
      setValues(
        Object.values(CheckType).reduce((acc: Values, cur) => {
          acc[cur] = checked;
          return acc;
        }, {})
      );
    } else {
      setValues((prev) => ({
        ...prev,
        [name]: checked,
      }));
    }
  };

  const onClick = () => onComplete(!!values.isMarketingAgreed);

  const isValid =
    values.isAgeAgreed && values.isPrivacyAgreed && values.isServiceAgreed;
  const isAllChecked =
    Object.values(values).filter((x) => x).length ===
    Object.values(CheckType).length;

  return (
    <Form {...props}>
      <Flexbox>
        <CheckboxItem gap="12px">
          <Checkbox
            checkType="circle"
            variant="large"
            checked={isAllChecked}
            name="allCheck"
            onChange={onCheck}
          />
          <CheckboxLabel variant="medium16">
            <Typography color="#191E23" fontWeight={600}>
              전체 동의
            </Typography>
          </CheckboxLabel>
        </CheckboxItem>
      </Flexbox>
      <Divider />
      <Flexbox>
        <CheckboxItem gap="12px">
          <Checkbox
            checkType="circle"
            variant="large"
            checked={values.isAgeAgreed}
            name="isAgeAgreed"
            onChange={onCheck}
          />
          <CheckboxLabel variant="medium16">
            (필수) 만 14세 이상 확인
          </CheckboxLabel>
        </CheckboxItem>
      </Flexbox>
      <Flexbox>
        <CheckboxItem gap="12px">
          <Checkbox
            checkType="circle"
            variant="large"
            checked={values.isServiceAgreed}
            name="isServiceAgreed"
            onChange={onCheck}
          />
          <CheckboxLabel variant="medium16">
            (필수) 서비스 이용 약관 동의
          </CheckboxLabel>
        </CheckboxItem>
        <FlexFullGrow />
        <a href={POLICY_LINKS.policy} target="_blank" rel="noreferrer">
          <TextButton>내용보기</TextButton>
        </a>
      </Flexbox>
      <Flexbox>
        <CheckboxItem gap="12px">
          <Checkbox
            checkType="circle"
            variant="large"
            checked={values.isPrivacyAgreed}
            name="isPrivacyAgreed"
            onChange={onCheck}
          />
          <CheckboxLabel variant="medium16">
            (필수) 개인정보 수집 및 이용 동의
          </CheckboxLabel>
        </CheckboxItem>
        <FlexFullGrow />
        <a href={POLICY_LINKS.private} target="_blank" rel="noreferrer">
          <TextButton>내용보기</TextButton>
        </a>
      </Flexbox>
      <Flexbox>
        <CheckboxItem gap="12px">
          <Checkbox
            checkType="circle"
            variant="large"
            checked={values.isMarketingAgreed}
            name="isMarketingAgreed"
            onChange={onCheck}
          />
          <CheckboxLabel variant="medium16">
            (선택) 마케팅 정보 수신 동의
          </CheckboxLabel>
        </CheckboxItem>
        <FlexFullGrow />
        <a href={POLICY_LINKS.marketing} target="_blank" rel="noreferrer">
          <TextButton>내용보기</TextButton>
        </a>
      </Flexbox>
      <FlexFullGrow />
      <Button
        disabled={!isValid || loading}
        variant="fill"
        color="primary"
        size="mediumLarge"
        fullWidth
        onClick={onClick}
      >
        {loading ? '가입 중...' : '동의하고 가입 완료'}
      </Button>
    </Form>
  );
};

const Form = styled(InputContainer)`
  gap: 20px;
`;

const TextButton = styled.span`
  ${{...typographyStyle.medium12}}
  cursor: pointer;
  color: #969b9e;
  text-decoration: underline;
`;
